<template>
  <validated-form name="orderDetails">
    <div class="cf-flex cf-flex--column">
      <!-- Order Type Tabs -->
      <div class="cf-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.label"
          :class="['cf-tab', { 'cf-tab--active': fulfillmentType === tab.fulfillmentType }]"
          type="button"
          @click="selectFulfillmentType(tab.fulfillmentType)"
        >
          <img
            v-if="tab.imageUrl"
            :src="tab.imageUrl"
            :alt="tab.label"
            class="cf-order-mode-image"
          >
          <span
            v-else
            :class="`cf-icon far ${tab.icon}`"
          />

          {{ tab.label }}
        </button>
      </div>

      <div
        v-if="order.orderItems.length > 0"
        class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-center cf-flex--gap-10 cf-alt-background cf-margin-top-16"
      >
        <span class="cf-icon cf-icon--red far fa-exclamation-circle" />

        <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
          Changing your location or mode may adjust your cart.
        </p>
      </div>

      <!-- Location Selector -->
      <cf-location-selector
        v-if="['pick-up', 'room-service'].includes(fulfillmentType)"
        :is-selecting="isUpdatingOrderDetails"
        :show-selected="fulfillmentType === orderDetails.fulfillmentType"
        :selected-store-id="selectedLocation.storeId"
        inline-search-results
        @select="updateLocationDetails"
      />

      <!-- Delivery and Shipping Forms -->
      <div
        v-else-if="['delivery', 'shipping'].includes(fulfillmentType)"
        class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-top-16"
      >
        <cf-delivery-address-form
          v-show="!deliveryDetails"
          :full-name-required="fulfillmentType === 'shipping'"
          :is-processing="isCalculatingDeliveryEstimate"
          :title="`${fulfillmentType === 'shipping' ? 'Shipping' : 'Delivery'} Address`"
          @selected="submitAddress"
        />

        <!-- Populated Delivery and Shipping Forms -->
        <div
          v-if="deliveryDetails"
          class="animation-slide-left"
        >
          <div class="cf-flex cf-flex--column cf-flex--gap-16">
            <div class="cf-flex cf-flex--column cf-flex--gap-8">
              <h3 class="cf-title cf-title--3">
                {{ fulfillmentType === 'shipping' ? 'Shipping' : 'Delivered' }} From
              </h3>

              <div class="cf-card cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-20">
                <span class="cf-icon far fa-map-marker-alt" />

                <div class="cf-flex cf-flex--column cf-flex--gap-2 cf-text-align-left">
                  <h4 class="cf-title cf-title--4">
                    {{ deliveryDetails.location.displayName || deliveryDetails.location.description }}
                  </h4>

                  <p class="cf-subtitle cf-subtitle--2">
                    {{ deliveryLocationAddress }}
                  </p>
                </div>
              </div>
            </div>

            <div class="cf-flex cf-flex--column cf-flex--gap-8">
              <h3 class="cf-title cf-title--3">
                {{ fulfillmentType === 'shipping' ? 'Shipping' : 'Delivered' }} To
              </h3>

              <div class="cf-card cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-20">
                <span class="cf-icon far fa-map-marker-alt" />

                <div class="cf-flex cf-flex--column cf-flex--gap-2 cf-text-align-left">
                  <h4
                    v-if="deliveryDetails.address.type"
                    class="cf-title cf-title--4"
                  >
                    {{ deliveryDetails.address.type }}
                  </h4>

                  <p
                    v-for="(line, i) in deliveryAddressLines"
                    :key="`delivery-${i}`"
                    class="cf-subtitle cf-subtitle--2"
                  >
                    {{ line }}
                  </p>
                </div>

                <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-end cf-flex--grow-1 cf-flex--gap-8">
                  <button
                    class="cf-button cf-button--tertiary cf-button--xs"
                    type="button"
                    title="Change Address"
                    @click="changeDeliveryAddress"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>

            <div class="cf-flex cf-flex--row cf-flex--gap-16 cf-padding">
              <div
                v-if="!isCateringDeliveryOrderType"
                class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-16 cf-flex--grow-1"
              >
                <template v-if="fulfillmentType === 'shipping'">
                  <p class="cf-subtitle cf-subtitle--2">
                    Shipping options will be provided at checkout.
                  </p>
                </template>

                <template v-else>
                  <span class="cf-icon far fa-clock dark-gray" />

                  <p class="cf-subtitle cf-subtitle--2">
                    Delivery ETA:

                    <span class="cf-subtitle--heavy">
                      {{ deliveryDetails.estimate.etaDisplay }}
                    </span>
                  </p>
                </template>
              </div>

              <button
                :class="['cf-button cf-button--primary cf-button--lg cf-button--auto', { 'cf-button--loading': isUpdatingOrderDetails }]"
                type="button"
                @click="updateLocationDetails"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </validated-form>
</template>



<script>
  import { mapGetters } from 'vuex';

  import delivery from '@/services/delivery';
  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';
  import shipping from '@/services/shipping';
  import { formatAddress, formatAddressInline } from '@/helpers/address-format';

  import CfLocationSelector from '@/view/components/globals/cf-location-selector/index.vue';
  import CfDeliveryAddressForm from '@/view/components/globals/cf-delivery-address-form/index.vue';



  export default {
    name: 'CfEditOrderAddressDetails',

    components: {
      CfLocationSelector,
      CfDeliveryAddressForm
    },

    props: {
      overrideFulfillmentType: {
        type: String,
        default: null
      },

      editSuccessCallbackHandler: {
        type: Function,
        default: () => {}
      }
    },

    data() {
      return {
        deliveryDetails: null,
        fulfillmentType: null,
        isCalculatingDeliveryEstimate: false,
        isUpdatingOrderDetails: false
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order']),

      deliveryLocationAddress() {
        const addr = this.deliveryDetails?.location?.address;
        if (!addr) return '';
        return formatAddressInline(addr, addr.countryCode);
      },

      deliveryAddressLines() {
        const addr = this.deliveryDetails?.address;
        if (!addr) return [];
        return formatAddress(addr, addr.countryCode);
      },
      ...mapGetters('session', ['orderDetails']),
      ...mapGetters('content', ['content']),

      isCateringOrderType() {
        return this.orderDetails.orderType === 'catering';
      },

      isCateringDeliveryOrderType() {
        return this.isCateringOrderType && this.fulfillmentType === 'delivery';
      },

      tabs() {
        const isCateringDeliverySupported = this.merchant.features['catering-delivery'];
        const { metadata } = this.content.OrderModes;

        if (this.isCateringOrderType) {
          return [
            {
              fulfillmentType: 'pick-up',
              label: metadata.cateringPickupLabel,
              icon: 'fa-shopping-bag',
              imageUrl: metadata.cateringPickupImageUrl
            },
            isCateringDeliverySupported && {
              fulfillmentType: 'delivery',
              label: metadata.cateringDeliveryLabel,
              icon: 'fa-car',
              imageUrl: metadata.cateringDeliveryImageUrl
            }
          ].filter(Boolean);
        }

        return [
          {
            fulfillmentType: 'pick-up',
            label: metadata.menuTypeLabelPickup,
            icon: 'fa-shopping-bag',
            imageUrl: metadata.pickupImageUrl
          },
          this.merchant.features.delivery && {
            fulfillmentType: 'delivery',
            label: metadata.menuTypeLabelDelivery,
            icon: 'fa-car',
            imageUrl: metadata.deliveryImageUrl
          },
          this.merchant.features['room-service'] && {
            fulfillmentType: 'room-service',
            label: metadata.menuTypeLabelRoomService,
            icon: 'fa-door-closed',
            imageUrl: metadata.roomServiceImageUrl
          },
          this.merchant.features.shipping && {
            fulfillmentType: 'shipping',
            label: metadata.menuTypeLabelShipping,
            icon: 'fa-truck',
            imageUrl: metadata.shippingImageUrl
          }
        ].filter(Boolean);
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      changeDeliveryAddress() {
        this.deliveryDetails = null;
      },

      createdHandler() {
        this.selectFulfillmentType(this.overrideFulfillmentType || this.orderDetails.fulfillmentType || 'pick-up');
      },

      selectFulfillmentType(type) {
        this.deliveryDetails = null;
        this.fulfillmentType = type;

        this.$store.dispatch('locations/updateSearchFilters', {
          orderType: this.orderDetails.orderType,
          fulfillmentType: type
        });
      },

      submitAddress(address) {
        if (this.fulfillmentType === 'shipping') {
          this.submitShippingAddress(address);
        }

        else {
          this.submitDeliveryAddress(address);
        }
      },

      async submitDeliveryAddress(address) {
        try {
          this.isCalculatingDeliveryEstimate = true;

          const { location, addressGeocodeData } = await delivery.getClosestDeliveryLocation({ address });

          const menuType = this.isCateringOrderType ? 'Catering' : 'Delivery';

          const estimate = await delivery.getEstimate({
            address,
            addressGeocodeData,
            location,
            merchant: this.merchant,
            order: this.order,
            menuType
          });

          this.deliveryDetails = {
            address,
            addressGeocodeData,
            estimate,
            location
          };
        }

        catch (error) {
          errorHandlers.silent(error);

          this.$closeModal('editOrderDetails');

          this.$openModal('deliveryNotAvailable');
        }

        finally {
          this.isCalculatingDeliveryEstimate = false;
        }
      },

      async submitShippingAddress(address) {
        try {
          this.isCalculatingDeliveryEstimate = true;

          const location = await shipping.getClosestShippingLocation({
            address
          });

          await shipping.validateAddress({
            address,
            location
          });

          this.deliveryDetails = {
            address,
            estimate: null,
            location
          };
        }

        catch (error) {
          errorHandlers.silent(error);

          this.$closeModal('editOrderDetails');
          this.$openModal('deliveryNotAvailable', {
            shipping: true
          });
        }

        finally {
          this.isCalculatingDeliveryEstimate = false;
        }
      },

      updateLocationDetails(location) {
        const content = this.content.ClearOrderOnLocationChange;

        const onConfirm = () => {
          this.$store.dispatch('orders/cancelOrder');
          this.$store.dispatch('locations/selectLocation', { location, withMenu: true, isOrderReset: true });
        };

        const onConfirmSuccess = () => this.$closeAllModals();

        const confirmationModalData = {
          title: content.title,
          primaryText: content.description,
          ctaConfirmText: content.metadata.primaryCta,
          ctaCancelText: content.metadata.secondaryCta,
          onConfirm,
          onConfirmSuccess
        };

        if (this.merchant.options.clearOrderOnChangeLocation && this.order.orderId) {
          this.$openModal('confirmation', confirmationModalData);
        }
        else {
          this.updateOrderWithDetails(location);
        }
      },

      async updateOrderWithDetails(location) {
        try {
          this.isUpdatingOrderDetails = true;

          const dispatchPayload = {
            fulfillmentType: this.fulfillmentType,
            location,
            menuType: null,
            fulfillmentTime: null
          };

          if (this.deliveryDetails) {
            const deliveryDetails = {
              location: this.deliveryDetails.location,
              deliveryAddress: this.deliveryDetails.address,
              deliveryEstimate: this.deliveryDetails.estimate,
              deliveryAddressGeoCoordinates: this.deliveryDetails.addressGeocodeData
            };

            Object.assign(dispatchPayload, deliveryDetails);
          }

          await this.$store.dispatch('orders/updateOrder', dispatchPayload);

          this.$closeModal('editOrderDetails');

          const message = this.order.orderId ? 'Selected location and cart were updated.' : 'Selected location was updated.';

          events.emit('notification', { type: 'success', message });

          this.editSuccessCallbackHandler();
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.isUpdatingOrderDetails = false;
        }
      }
    }
  };
</script>
